import { useMemo, useState } from "react";
import { Check, Minus, X, Sparkles, TrendingUp, Clock, Wallet } from "lucide-react";

type Props = {
  isBn: boolean;
  displayFont: string;
  bodyFont: string;
};

const bnDigits = (s: string | number) =>
  String(s).replace(/[0-9]/g, (d) => "০১২৩৪৫৬৭৮৯"[+d]);
const fmt = (n: number, bn: boolean) => {
  const s = Math.round(n).toLocaleString("en-IN");
  return bn ? bnDigits(s) : s;
};

const Kicker = ({ children }: { children: React.ReactNode }) => (
  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-gray-200 text-[11px] uppercase tracking-[0.14em] text-[#1ba179] mb-5">
    <Sparkles className="w-3 h-3" />
    {children}
  </div>
);

const Serif = ({ children, displayFont }: { children: React.ReactNode; displayFont: string }) => (
  <span className="italic font-normal text-[#0f172a]" style={{ fontFamily: displayFont }}>
    {children}
  </span>
);

export default function RoiAndCompare({ isBn, displayFont, bodyFont }: Props) {
  const [patients, setPatients] = useState(30);
  const [fee, setFee] = useState(700);
  const [days, setDays] = useState(24);

  const stats = useMemo(() => {
    const monthlyRevenue = patients * fee * days;
    // Assumptions: 12% no-show reduction from WhatsApp reminders,
    // 8% extra visits from care-gap recall, 15 min saved per Rx.
    const noShowRecovered = monthlyRevenue * 0.12;
    const recallRevenue = monthlyRevenue * 0.08;
    const timeSavedHours = (patients * days * 15) / 60;
    const total = noShowRecovered + recallRevenue;
    return { monthlyRevenue, noShowRecovered, recallRevenue, timeSavedHours, total };
  }, [patients, fee, days]);

  return (
    <>
      {/* ============ ROI CALCULATOR ============ */}
      <section
        id="roi"
        className="max-w-7xl mx-auto mt-24 md:mt-40"
        style={{ fontFamily: bodyFont }}
      >
        <div className="rounded-[2.5rem] overflow-hidden border border-gray-200 bg-gradient-to-br from-white via-[#f8fafc] to-[#e6faf3]">
          <div className="grid grid-cols-1 lg:grid-cols-12">
            {/* Left — copy + sliders */}
            <div className="lg:col-span-6 p-8 md:p-14">
              <Kicker>{isBn ? "রিটার্ন অন ইনভেস্টমেন্ট" : "ROI Calculator"}</Kicker>
              <h2 className="text-4xl md:text-5xl leading-[1.05]" style={{ fontFamily: displayFont }}>
                {isBn ? "প্রতি মাসে " : "See how much you "}
                <Serif displayFont={displayFont}>
                  {isBn ? "কত টাকা বাড়বে?" : "actually earn back."}
                </Serif>
              </h2>
              <p className="mt-5 text-[15px] text-[#64748b] leading-relaxed max-w-md">
                {isBn
                  ? "স্লাইডার সরিয়ে দেখুন — WhatsApp রিমাইন্ডার, কেয়ার-গ্যাপ রিকল ও ডিজিটাল Rx দিয়ে প্রতি মাসে সম্ভাব্য অতিরিক্ত আয়।"
                  : "Move the sliders — estimated monthly upside from WhatsApp reminders, care-gap recall and digital prescriptions."}
              </p>

              <div className="mt-10 space-y-7">
                <Slider
                  label={isBn ? "প্রতিদিন রোগী" : "Patients per day"}
                  value={patients} min={5} max={80} step={1}
                  display={fmt(patients, isBn)}
                  onChange={setPatients}
                />
                <Slider
                  label={isBn ? "গড় ফি (৳)" : "Average fee (৳)"}
                  value={fee} min={200} max={3000} step={50}
                  display={"৳ " + fmt(fee, isBn)}
                  onChange={setFee}
                />
                <Slider
                  label={isBn ? "মাসে কর্মদিবস" : "Working days per month"}
                  value={days} min={10} max={30} step={1}
                  display={fmt(days, isBn)}
                  onChange={setDays}
                />
              </div>
            </div>

            {/* Right — results */}
            <div className="lg:col-span-6 p-8 md:p-14 bg-[#0f172a] text-white flex flex-col justify-center">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#9ce6cd] mb-3">
                {isBn ? "সম্ভাব্য মাসিক প্রভাব" : "Estimated monthly impact"}
              </div>
              <div
                className="text-6xl md:text-7xl leading-none font-bold tracking-tight"
                style={{ fontFamily: displayFont }}
              >
                ৳ {fmt(stats.total, isBn)}
              </div>
              <div className="mt-2 text-[13px] text-[#94a3b8]">
                {isBn ? "প্রতি মাসে অতিরিক্ত আয়ের সম্ভাবনা" : "additional revenue captured, per month"}
              </div>

              <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <MetricCard
                  icon={<TrendingUp className="w-4 h-4" />}
                  label={isBn ? "নো-শো পুনরুদ্ধার" : "No-shows recovered"}
                  value={"৳ " + fmt(stats.noShowRecovered, isBn)}
                  sub="+12%"
                />
                <MetricCard
                  icon={<Wallet className="w-4 h-4" />}
                  label={isBn ? "কেয়ার-গ্যাপ রিকল" : "Care-gap recalls"}
                  value={"৳ " + fmt(stats.recallRevenue, isBn)}
                  sub="+8%"
                />
                <MetricCard
                  icon={<Clock className="w-4 h-4" />}
                  label={isBn ? "সময় সাশ্রয়" : "Hours saved"}
                  value={fmt(stats.timeSavedHours, isBn) + " h"}
                  sub={isBn ? "প্রতি মাসে" : "per month"}
                />
              </div>

              <p className="mt-8 text-[11px] text-[#64748b] leading-relaxed">
                {isBn
                  ? "*বাংলাদেশের প্র্যাকটিস গড় ও অভ্যন্তরীণ অ্যানোনিমাইজড ডেটার ভিত্তিতে অনুমান।"
                  : "*Estimates based on Bangladeshi practice averages and anonymised internal benchmarks."}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ COMPARISON ============ */}
      <section
        id="compare"
        className="max-w-7xl mx-auto mt-24 md:mt-40"
        style={{ fontFamily: bodyFont }}
      >
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="flex justify-center"><Kicker>{isBn ? "তুলনা" : "How we compare"}</Kicker></div>
          <h2 className="text-4xl md:text-6xl leading-[1.05]" style={{ fontFamily: displayFont }}>
            <Serif displayFont={displayFont}>
              {isBn ? "কাগজ, সাধারণ EMR — নাকি Doctorsx?" : "Paper, generic EMR — or Doctorsx?"}
            </Serif>
          </h2>
          <p className="mt-5 text-[15px] text-[#64748b]">
            {isBn
              ? "বাংলাদেশের প্র্যাকটিসের জন্য ডিজাইন — প্রতিটি ওয়ার্কফ্লোতে।"
              : "Purpose-built for Bangladeshi practices — feature by feature."}
          </p>
        </div>

        <div className="rounded-3xl border border-gray-200 overflow-hidden bg-white">
          <div className="grid grid-cols-4 text-[13px]">
            <HeaderCell />
            <HeaderCell title={isBn ? "কাগজে" : "Paper / Diary"} muted />
            <HeaderCell title={isBn ? "সাধারণ EMR" : "Generic EMR"} muted />
            <HeaderCell title="Doctorsx" highlight displayFont={displayFont} />
          </div>

          {(isBn ? ROWS_BN : ROWS_EN).map((row, i) => (
            <div
              key={row.label}
              className={`grid grid-cols-4 text-[13px] ${i % 2 ? "bg-gray-50/60" : "bg-white"} border-t border-gray-100`}
            >
              <div className="px-5 py-4 text-[#0f172a] font-medium">{row.label}</div>
              <Cell v={row.paper} />
              <Cell v={row.emr} />
              <Cell v={row.us} highlight />
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

function Slider({
  label, value, min, max, step, display, onChange,
}: { label: string; value: number; min: number; max: number; step: number; display: string; onChange: (n: number) => void }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[13px] text-[#64748b]">{label}</span>
        <span className="text-[15px] font-semibold text-[#0f172a] tabular-nums">{display}</span>
      </div>
      <input
        type="range"
        min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-1.5 rounded-full appearance-none bg-gray-200 accent-[#1ba179] cursor-pointer"
      />
    </div>
  );
}

function MetricCard({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 backdrop-blur p-4">
      <div className="flex items-center gap-1.5 text-[#9ce6cd] mb-2">{icon}<span className="text-[10px] uppercase tracking-widest">{sub}</span></div>
      <div className="text-[16px] font-semibold tabular-nums">{value}</div>
      <div className="text-[11px] text-[#94a3b8] mt-0.5">{label}</div>
    </div>
  );
}

type V = true | false | "partial";
function Cell({ v, highlight = false }: { v: V; highlight?: boolean }) {
  return (
    <div className={`px-5 py-4 flex items-center ${highlight ? "bg-[#e6faf3]" : ""}`}>
      {v === true ? (
        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${highlight ? "bg-[#1ba179] text-white" : "bg-emerald-100 text-emerald-700"}`}>
          <Check className="w-3.5 h-3.5" strokeWidth={3} />
        </span>
      ) : v === "partial" ? (
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 text-amber-700">
          <Minus className="w-3.5 h-3.5" strokeWidth={3} />
        </span>
      ) : (
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-400">
          <X className="w-3.5 h-3.5" strokeWidth={3} />
        </span>
      )}
    </div>
  );
}

function HeaderCell({ title, highlight, muted, displayFont }: { title?: string; highlight?: boolean; muted?: boolean; displayFont?: string }) {
  return (
    <div
      className={`px-5 py-5 text-[13px] font-semibold uppercase tracking-[0.14em] ${
        highlight ? "bg-[#0f172a] text-white" : muted ? "text-[#64748b] bg-white" : "bg-white"
      }`}
      style={highlight && displayFont ? { fontFamily: displayFont, letterSpacing: 0 } : undefined}
    >
      {highlight ? <span className="text-[16px] normal-case tracking-normal">{title}</span> : title}
    </div>
  );
}

const ROWS_EN: { label: string; paper: V; emr: V; us: V }[] = [
  { label: "Bengali prescription pads (Rx)",   paper: true,      emr: false,     us: true },
  { label: "WhatsApp appointment agent",       paper: false,     emr: false,     us: true },
  { label: "Care-gap recall reminders",        paper: false,     emr: "partial", us: true },
  { label: "AI ambient scribe (Bangla SOAP)",  paper: false,     emr: false,     us: true },
  { label: "bKash / Nagad / SSLCommerz",       paper: false,     emr: "partial", us: true },
  { label: "Multi-chamber schedules",          paper: "partial", emr: "partial", us: true },
  { label: "Offline-first desktop app",        paper: false,     emr: false,     us: true },
  { label: "BMDC-compliant certificates",      paper: "partial", emr: false,     us: true },
  { label: "Patient portal + public profile",  paper: false,     emr: "partial", us: true },
  { label: "Setup time",                       paper: true,      emr: false,     us: true },
];

const ROWS_BN: { label: string; paper: V; emr: V; us: V }[] = [
  { label: "বাংলা প্রেসক্রিপশন প্যাড",           paper: true,      emr: false,     us: true },
  { label: "WhatsApp অ্যাপয়েন্টমেন্ট এজেন্ট",   paper: false,     emr: false,     us: true },
  { label: "কেয়ার-গ্যাপ রিকল রিমাইন্ডার",       paper: false,     emr: "partial", us: true },
  { label: "AI অ্যাম্বিয়েন্ট স্ক্রাইব (বাংলা SOAP)", paper: false,     emr: false,     us: true },
  { label: "bKash / Nagad / SSLCommerz",       paper: false,     emr: "partial", us: true },
  { label: "মাল্টি-চেম্বার সময়সূচি",              paper: "partial", emr: "partial", us: true },
  { label: "অফলাইন-ফার্স্ট ডেস্কটপ অ্যাপ",         paper: false,     emr: false,     us: true },
  { label: "BMDC-কমপ্লায়েন্ট সার্টিফিকেট",       paper: "partial", emr: false,     us: true },
  { label: "পেশেন্ট পোর্টাল + পাবলিক প্রোফাইল",   paper: false,     emr: "partial", us: true },
  { label: "সেটআপের সময়",                     paper: true,      emr: false,     us: true },
];
